<template>
  <aside class="yt-slide" :class="{ 'is-collapsed': collapsed }">
    <div class="yt-slide__toolbar">
      <button
        type="button"
        class="yt-slide__collapse-btn"
        :aria-label="collapsed ? $t('profile.expandSidebar') : $t('profile.collapseSidebar')"
        :title="collapsed ? $t('profile.expandSidebar') : $t('profile.collapseSidebar')"
        @click="toggleCollapsed"
      >
        <i
          class="fa-solid"
          :class="collapsed ? 'fas fa-align-right' : 'fas fa-align-left'"
          aria-hidden="true"
        />
      </button>
    </div>

    <!-- <div class="yt-slide__identity">
      <span v-if="hasNotification" class="yt-slide__notify" :title="$t('profile.newNotification')" />
      <div class="yt-slide__channel-avatar" aria-hidden="true">@</div>
      <div v-show="!collapsed" class="yt-slide__channel-title">{{ $t('profile.yourChannel') }}</div>
      <div v-show="!collapsed" class="yt-slide__channel-handle">{{ channelHandle }}</div>
    </div> -->

    <section v-show="!collapsed" class="yt-slide__stats" aria-label="Profile stats">
      <div class="yt-slide__stats-head">
        <div class="yt-slide__stats-avatar">
          <img v-if="userAvatar" :src="userAvatar" :alt="displayName" />
          <span v-else>{{ avatarInitial }}</span>
        </div>
        <strong class="yt-slide__stats-name">{{ displayName }}</strong>
      </div>

      <div class="yt-slide__stats-row">
        <span class="yt-slide__stats-label">{{ $t('profile.totalPoints') }}</span>
        <span class="yt-slide__stats-points">
          <i class="fa-solid fa-coins" aria-hidden="true" />
          {{ formattedPoints }}
        </span>
      </div>

      <div class="yt-slide__stats-grid">
        <div class="yt-slide__stat">
          <span class="yt-slide__stat-label">{{ $t('profile.level') }}</span>
          <strong class="yt-slide__stat-value">{{ userLevel }}</strong>
        </div>
        <div class="yt-slide__stat">
          <span class="yt-slide__stat-label">{{ $t('profile.rank') }}</span>
          <strong class="yt-slide__stat-value yt-slide__stat-value--gold">
            <i class="fa-solid fa-star" aria-hidden="true" />
            {{ userRankLabel }}
          </strong>
        </div>
      </div>

      <div class="yt-slide__xp">
        <div class="yt-slide__xp-head">
          <span class="yt-slide__stat-label">{{ $t('profile.experience') }}</span>
          <button type="button" class="yt-slide__xp-link">{{ $t('profile.xpHelp') }}</button>
        </div>
        <div class="yt-slide__xp-bar" role="progressbar" :aria-valuenow="xpCurrent" :aria-valuemin="0" :aria-valuemax="xpMax">
          <span class="yt-slide__xp-fill" :style="{ width: `${xpPercent}%` }" />
          <span class="yt-slide__xp-text">{{ xpCurrent.toLocaleString() }} / {{ xpMax.toLocaleString() }}</span>
        </div>
      </div>
    </section>

    <nav class="yt-slide__nav">
      <template v-for="item in visibleMenus" :key="item.id">
        <div class="yt-slide__group">
          <a-tooltip
            :title="collapsed ? menuLabel(item.linkLabel) : ''"
            placement="right"
            :mouse-enter-delay="0.2"
          >
            <button
              v-if="hasChildren(item)"
              type="button"
              class="yt-slide__item"
              :class="{ 'is-active': isExpanded(item.id) || isRouteActive(item.slug) }"
              @click="onParentClick(item)"
            >
              <i :class="getIcon(item)" class="yt-slide__icon" aria-hidden="true" />
              <span v-show="!collapsed" class="yt-slide__label">{{ menuLabel(item.linkLabel) }}</span>
              <span v-show="!collapsed" class="yt-slide__toggle" aria-hidden="true">
                {{ isExpanded(item.id) ? '—' : '+' }}
              </span>
            </button>

            <router-link
              v-else
              :to="item.slug || HOME"
              class="yt-slide__item"
              :class="{ 'is-active': isRouteActive(item.slug) }"
            >
              <i :class="getIcon(item)" class="yt-slide__icon" aria-hidden="true" />
              <span v-show="!collapsed" class="yt-slide__label">{{ menuLabel(item.linkLabel) }}</span>
            </router-link>
          </a-tooltip>

          <div
            v-if="!collapsed && hasChildren(item) && isExpanded(item.id)"
            class="yt-slide__children"
          >
            <router-link
              v-for="child in item.children"
              :key="child.id"
              :to="child.slug || HOME"
              class="yt-slide__item yt-slide__item--child"
              :class="{ 'is-active': isRouteActive(child.slug) }"
            >
              <i :class="getIcon(child)" class="yt-slide__icon" aria-hidden="true" />
              <span class="yt-slide__label">{{ menuLabel(child.linkLabel) }}</span>
            </router-link>
          </div>
        </div>
      </template>
    </nav>

    <div class="yt-slide__footer">
      <a-tooltip
        v-for="item in footerMenus"
        :key="item.key"
        :title="collapsed ? item.label : ''"
        placement="right"
        :mouse-enter-delay="0.2"
      >
        <router-link
          :to="item.to"
          class="yt-slide__item"
          :class="{ 'is-active': isRouteActive(item.to) }"
        >
          <i :class="item.icon" class="yt-slide__icon" aria-hidden="true" />
          <span v-show="!collapsed" class="yt-slide__label">{{ item.label }}</span>
        </router-link>
      </a-tooltip>
    </div>
  </aside>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { apiRedis } from '@/api/index'
import { CUSTOMER_MENU_BUILDING } from '@/const/ApiRedis'
import commons from '@/utils/Common'
import {
  buildMenuTree,
  parseMenuPayload,
  type CustomerMenuItem,
} from '@/utils/customerMenu'
import { CONTACT, HOME, INFORM } from '@/const/path'

const { t, te } = useI18n()

type ProfileMenuItem = CustomerMenuItem

const emit = defineEmits<{
  (e: 'collapsed-change', value: boolean): void
}>()

const route = useRoute()
const blogsType = ref<ProfileMenuItem[]>([])
const isLogin = ref(false)
const userTagName = ref('')
const user = ref<Record<string, any>>({})
const collapsed = ref(true)
const expandedMap = ref<Record<number | string, boolean>>({})
const hasNotification = ref(true)

const avatarInitial = computed(() => {
  const name = (displayName.value || 'H').trim()
  return name.charAt(0).toUpperCase() || 'H'
})

const displayName = computed(
  () =>
    user.value.name ||
    user.value.fullName ||
    userTagName.value ||
    t('profile.account'),
)

const channelHandle = computed(() => {
  const raw =
    user.value.tagName ||
    user.value.tag ||
    user.value.phone ||
    user.value.mobile ||
    userTagName.value
  if (!raw) return '@—'
  return `@${String(raw).trim().replace(/^@/, '')}`
})

const userAvatar = computed(() => user.value.avatar || user.value.image || '')

const totalPoints = computed(() => Number(user.value.points ?? user.value.totalPoint ?? 241301.89))
const userLevel = computed(() => Number(user.value.level ?? 73))
const userRankLabel = computed(() => user.value.rankLabel || user.value.rank || t('profile.rankGold'))
const xpCurrent = computed(() => Number(user.value.xp ?? user.value.experience ?? 616576))
const xpMax = computed(() => Number(user.value.xpMax ?? user.value.experienceMax ?? 1640000))
const xpPercent = computed(() =>
  xpMax.value > 0 ? Math.min(100, Math.round((xpCurrent.value / xpMax.value) * 100)) : 0,
)

const formattedPoints = computed(() =>
  totalPoints.value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }),
)

const visibleMenus = computed(() =>
  blogsType.value.filter((item) => {
    if (item.slug === 'auth') return isLogin.value
    return true
  })
)

const footerMenus = computed(() => [
  {
    key: 'settings',
    to: INFORM,
    icon: 'fa-solid fa-gear',
    label: t('profile.settings'),
  },
  {
    key: 'feedback',
    to: CONTACT,
    icon: 'fa-solid fa-comment-dots',
    label: t('profile.sendFeedback'),
  },
])

watch(collapsed, (value) => {
  emit('collapsed-change', value)
})

onMounted(async () => {
  isLogin.value = !!commons.checkLogin()
  user.value = commons.getUserData() || {}
  userTagName.value =
    user.value.tagName || user.value.name || user.value.fullName || user.value.username || ''
  await getBlogsType()
  emit('collapsed-change', collapsed.value)
})

const hasChildren = (item: ProfileMenuItem) =>
  Array.isArray(item?.children) && item.children.length > 0

const isExpanded = (id: number | string) => expandedMap.value[id] === true

const toggleSection = (id: number | string) => {
  expandedMap.value[id] = !isExpanded(id)
}

const toggleCollapsed = () => {
  collapsed.value = !collapsed.value
  if (collapsed.value) {
    expandedMap.value = Object.fromEntries(
      Object.keys(expandedMap.value).map((id) => [id, false])
    )
  }
}

const onParentClick = (item: ProfileMenuItem) => {
  if (collapsed.value) {
    collapsed.value = false
    expandedMap.value[item.id] = true
    return
  }
  toggleSection(item.id)
}

const isRouteActive = (slug?: string) => {
  if (!slug || slug === 'auth') return false
  const path = route.path
  return path === slug || path.startsWith(`${slug}/`)
}

const getIcon = (item: ProfileMenuItem) => item?.icon || ''

const menuLabel = (linkLabel?: string) => {
  const label = String(linkLabel || '').trim()
  if (!label) return ''
  return te(label) ? t(label) : label
}

const getBlogsType = async () => {
  try {
    const res: any = await apiRedis(CUSTOMER_MENU_BUILDING)
    const payload = parseMenuPayload(res)
    blogsType.value = buildMenuTree(payload)
  } catch {
    blogsType.value = []
  }

  const visible = blogsType.value.filter((item) => {
    if (item.slug === 'auth') return isLogin.value
    return true
  })

  initExpandedSections(visible)
}

const initExpandedSections = (menus: ProfileMenuItem[]) => {
  expandedMap.value = Object.fromEntries(
    menus.map((item, index) => [item.id, index === 0 && hasChildren(item)])
  )
}
</script>

<style scoped>
.yt-slide {
  /* Inherit tenant profile tokens from .layout-default-profile */
  --sp-ink: var(--tp-ink, #172033);
  --sp-muted: var(--tp-muted, #64748b);
  --sp-light: var(--tp-light, #94a3b8);
  --sp-border: var(--tp-border, #e2e8f0);
  --sp-bg: var(--tp-bg, #ffffff);
  --sp-surface: var(--tp-surface, #f8fafc);
  --sp-primary: var(--tp-primary, #0f3d5e);
  --sp-primary-hover: var(--tp-primary-hover, #0c3148);
  --sp-accent: var(--tp-accent, #0e9f8a);
  --sp-accent-hover: var(--tp-accent-hover, #0b8572);
  --sp-orange: var(--tp-cta, #f59e0b);
  --sp-danger: var(--tp-danger, #dc2626);
  --sp-success: var(--tp-success, #0e9f8a);
  --sp-menu-hover: var(--tp-surface, #f8fafc);
  --sp-menu-active: var(--tp-primary-soft, #e8eef3);
  --sp-stats-bg: #222222;
  --sp-stats-border: color-mix(in srgb, #fff 12%, transparent);
  --sp-font: var(--tp-font, var(--default-font, 'Plus Jakarta Sans', system-ui, sans-serif));

  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 16px 12px 24px;
  background: var(--sp-bg);
  color: var(--sp-ink);
  font-family: var(--sp-font);
  transition: padding 0.2s ease;
}

.yt-slide.is-collapsed {
  padding: 16px 8px 24px;
  align-items: center;
}

.yt-slide__toolbar {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-bottom: 8px;
}

.yt-slide.is-collapsed .yt-slide__toolbar {
  justify-content: center;
}

.yt-slide__collapse-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: var(--tp-radius-control, 10px);
  background: transparent;
  color: var(--sp-muted);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.yt-slide__collapse-btn:hover {
  background: var(--sp-menu-hover);
  color: var(--sp-ink);
}

.yt-slide__identity {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 12px 18px;
  text-align: center;
}

.yt-slide.is-collapsed .yt-slide__identity {
  padding: 4px 0 14px;
}

.yt-slide__notify {
  position: absolute;
  top: 0;
  right: 12px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--sp-danger);
  box-shadow: 0 0 0 2px var(--sp-bg);
}

.yt-slide.is-collapsed .yt-slide__notify {
  right: 2px;
  top: -2px;
}

.yt-slide__channel-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  margin-bottom: 12px;
  border-radius: 50%;
  background: var(--sp-orange);
  color: var(--text-white, #fff);
  font-size: 34px;
  font-weight: 700;
  line-height: 1;
  user-select: none;
  box-shadow: 0 4px 14px color-mix(in srgb, var(--sp-orange) 28%, transparent);
  transition: width 0.2s ease, height 0.2s ease, font-size 0.2s ease, margin 0.2s ease;
}

.yt-slide.is-collapsed .yt-slide__channel-avatar {
  width: 40px;
  height: 40px;
  margin-bottom: 0;
  font-size: 17px;
  box-shadow: none;
}

.yt-slide__channel-title {
  margin-bottom: 2px;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;
  letter-spacing: -0.01em;
  color: var(--sp-ink);
}

.yt-slide__channel-handle {
  font-size: 13px;
  font-weight: 500;
  line-height: 1.35;
  color: var(--sp-muted);
}

.yt-slide__stats {
  width: 100%;
  margin-bottom: 14px;
  padding: 14px;
  border-radius: var(--tp-radius-section, 14px);
  background: var(--tp-dark-gradient, linear-gradient(160deg, #222, #1a1a1a));
  color: var(--text-white, #fff);
  border: 1px solid color-mix(in srgb, var(--sp-stats-bg) 88%, #000);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
}

.yt-slide__stats-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid var(--sp-stats-border);
}

.yt-slide__stats-avatar {
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  overflow: hidden;
  border-radius: 50%;
  background: color-mix(in srgb, var(--text-white, #fff) 14%, transparent);
  color: var(--text-white, #fff);
  font-size: 15px;
  font-weight: 700;
  display: grid;
  place-items: center;
  border: 1px solid var(--sp-stats-border);
}

.yt-slide__stats-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.yt-slide__stats-name {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.35;
  letter-spacing: -0.01em;
  color: var(--text-white, #fff);
  word-break: break-word;
}

.yt-slide__stats-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid var(--sp-stats-border);
}

.yt-slide__stats-label,
.yt-slide__stat-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: color-mix(in srgb, var(--text-white, #fff) 58%, transparent);
}

.yt-slide__stats-points {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 700;
  color: var(--text-white, #fff);
  white-space: nowrap;
}

.yt-slide__stats-points i {
  color: var(--sp-orange);
  font-size: 13px;
}

.yt-slide__stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid var(--sp-stats-border);
}

.yt-slide__stat {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.yt-slide__stat-value {
  font-size: 20px;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: var(--text-white, #fff);
}

.yt-slide__stat-value--gold {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 15px;
  font-weight: 700;
  color: var(--sp-orange);
}

.yt-slide__stat-value--gold i {
  font-size: 13px;
}

.yt-slide__xp-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.yt-slide__xp-link {
  border: 0;
  padding: 0;
  background: transparent;
  color: color-mix(in srgb, var(--text-white, #fff) 55%, transparent);
  font-family: inherit;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-align: right;
  cursor: pointer;
  line-height: 1.3;
}

.yt-slide__xp-link:hover {
  color: color-mix(in srgb, var(--text-white, #fff) 82%, transparent);
}

.yt-slide__xp-bar {
  position: relative;
  height: 26px;
  border-radius: 999px;
  overflow: hidden;
  background: color-mix(in srgb, #000 28%, var(--sp-stats-bg));
}

.yt-slide__xp-fill {
  position: absolute;
  inset: 0 auto 0 0;
  border-radius: inherit;
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--sp-accent) 80%, #fff),
    var(--sp-accent)
  );
  transition: width 0.3s ease;
}

.yt-slide__xp-text {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 11px;
  font-weight: 700;
  color: var(--text-white, #fff);
  letter-spacing: 0.01em;
}

.yt-slide__nav {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  width: 100%;
}

.yt-slide__footer {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  margin-top: 20px;
  padding-top: 14px;
  border-top: 1px solid var(--sp-border);
}

.yt-slide__group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.yt-slide__item {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  min-height: 40px;
  padding: 0 12px;
  border: 0;
  border-radius: var(--tp-radius-control, 10px);
  background: transparent;
  color: var(--sp-ink);
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.35;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, padding 0.2s ease;
}

.yt-slide.is-collapsed .yt-slide__item {
  justify-content: center;
  gap: 0;
  padding: 0;
  width: 40px;
  min-height: 40px;
  margin-inline: auto;
}

.yt-slide__item:hover {
  background: var(--sp-menu-hover);
  color: var(--sp-primary);
}

.yt-slide__item.is-active {
  background: var(--sp-menu-active);
  color: var(--sp-primary);
  font-weight: 650;
  box-shadow: inset 3px 0 0 var(--sp-primary);
}

.yt-slide__item--child {
  padding-left: 20px;
  font-size: 13px;
}

.yt-slide__icon {
  flex-shrink: 0;
  width: 20px;
  font-size: 15px;
  text-align: center;
  color: var(--sp-muted);
}

.yt-slide__item.is-active .yt-slide__icon,
.yt-slide__item:hover .yt-slide__icon {
  color: var(--sp-primary);
}

.yt-slide__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.yt-slide__toggle {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  color: var(--sp-light);
}

.yt-slide__children {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 4px;
}

@media (max-width: 991px) {
  .yt-slide {
    padding: 20px 10px;
  }

  .yt-slide__channel-avatar {
    width: 68px;
    height: 68px;
    font-size: 28px;
  }
}
</style>
